/**
 * Tool Card Component
 * Marketplace-style card for displaying tools with category badges,
 * pricing tier indicators, vendor info, and external links
 */

import { Link } from "react-router-dom";
import { cn } from "@utils/cn";
import { getDifficultyStyles, formatTopic } from "@utils/icons";
import type {
  LibraryDifficulty,
  LibraryResource,
  LibraryResourceWithUserData,
} from "@/types/models";
import type { Tool, ToolWithUserData } from "@/types/tools";
import { BookmarkButton } from "@components/library/BookmarkButton";
import { ProgressIcon } from "@components/library/ProgressIndicator";
import { PricingBadge } from "./PricingBadge";
import { VendorLabel } from "./VendorBadge";
import { IntegrationTypeBadge } from "./IntegrationTypeBadge";
import { AverageRatingBadge } from "./AverageRatingBadge";

type ToolType =
  | LibraryResource
  | LibraryResourceWithUserData
  | Tool
  | ToolWithUserData;

interface ToolCardProps {
  tool: ToolType;
  /** If true, shows bookmark button and progress indicator (requires resource to have user data) */
  showUserData?: boolean;
}

// External link icon
function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
      />
    </svg>
  );
}

// Wrench/Tool icon
function ToolIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z"
      />
    </svg>
  );
}

// Type guard to check if resource has user data
function hasUserData(
  resource: ToolType,
): resource is LibraryResourceWithUserData | ToolWithUserData {
  return "isBookmarked" in resource;
}

// Type guard to check if resource has tool marketplace data
function hasToolData(resource: ToolType): resource is Tool | ToolWithUserData {
  return (
    "pricingTier" in resource ||
    "vendor" in resource ||
    "integrationType" in resource
  );
}

// Eye icon for view count
function EyeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );
}

// Map topic slugs to category display
const categoryMap: Record<string, { label: string; color: string }> = {
  // Technical categories
  "agent-fundamentals": {
    label: "Agent Building",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  },
  "tools-integrations": {
    label: "Integrations",
    color:
      "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  },
  deployment: {
    label: "Deployment",
    color:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  },
  "best-practices": {
    label: "Best Practices",
    color: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  },
  // Healthcare categories
  "clinical-tools": {
    label: "Clinical",
    color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  },
  "revenue-cycle": {
    label: "Revenue Cycle",
    color:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  },
  "admin-operations": {
    label: "Admin",
    color:
      "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  },
  "personal-productivity": {
    label: "Productivity",
    color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
  },
};

function getCategoryInfo(topic: string): { label: string; color: string } {
  return (
    categoryMap[topic] || {
      label: formatTopic(topic),
      color: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
    }
  );
}

export function ToolCard({ tool, showUserData = false }: ToolCardProps) {
  const isExternal = "source" in tool && tool.source === "external";
  const toolWithUserData = hasUserData(tool) ? tool : null;
  const toolData = hasToolData(tool) ? tool : null;

  // Get primary category from first topic
  const primaryCategory =
    tool.topics.length > 0 ? getCategoryInfo(tool.topics[0]) : null;

  const cardContent = (
    <div className="h-full flex flex-col">
      {/* Header with icon and category */}
      <div className="p-5 pb-3">
        <div className="flex items-start justify-between mb-3">
          {/* Tool icon */}
          <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-900/40 dark:to-primary-800/20 rounded-xl flex items-center justify-center shadow-sm">
            <ToolIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>

          {/* Right side badges */}
          <div className="flex items-center gap-2">
            {/* Progress indicator */}
            {showUserData && toolWithUserData && (
              <ProgressIcon status={toolWithUserData.progressStatus} />
            )}
            {/* Pricing tier badge */}
            {toolData?.pricingTier && (
              <PricingBadge tier={toolData.pricingTier} size="sm" />
            )}
            {/* Difficulty badge */}
            <span
              className={cn(
                "px-2 py-0.5 text-xs font-medium rounded-full capitalize",
                getDifficultyStyles(tool.difficulty as LibraryDifficulty),
              )}
            >
              {tool.difficulty}
            </span>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1.5 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors line-clamp-1">
          {tool.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 min-h-[2.5rem]">
          {tool.description}
        </p>
      </div>

      {/* Categories */}
      <div className="px-5 pb-3 flex-grow">
        <div className="flex flex-wrap gap-1.5">
          {primaryCategory && (
            <span
              className={cn(
                "px-2.5 py-1 text-xs font-medium rounded-lg",
                primaryCategory.color,
              )}
            >
              {primaryCategory.label}
            </span>
          )}
          {tool.topics.slice(1, 2).map((topic) => {
            const info = getCategoryInfo(topic);
            return (
              <span
                key={topic}
                className={cn(
                  "px-2.5 py-1 text-xs font-medium rounded-lg",
                  info.color,
                )}
              >
                {info.label}
              </span>
            );
          })}
          {tool.topics.length > 2 && (
            <span className="px-2.5 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg">
              +{tool.topics.length - 2}
            </span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700/50 mt-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            {/* Vendor info takes priority over author */}
            {toolData?.vendor ? (
              <VendorLabel vendor={toolData.vendor} />
            ) : tool.author ? (
              <span className="truncate max-w-[120px]">{tool.author}</span>
            ) : null}

            {/* Integration type */}
            {toolData?.integrationType && (
              <IntegrationTypeBadge
                type={toolData.integrationType}
                size="sm"
                showLabel={false}
              />
            )}

            {/* View count */}
            {toolData?.viewCount !== undefined && toolData.viewCount > 0 && (
              <span className="flex items-center gap-1 text-gray-400 dark:text-gray-500">
                <EyeIcon className="w-3.5 h-3.5" />
                <span className="text-xs">{toolData.viewCount}</span>
              </span>
            )}

            {/* Rating badge */}
            {toolData &&
              "ratingCount" in toolData &&
              toolData.ratingCount > 0 && (
                <AverageRatingBadge
                  averageRating={toolData.averageRating}
                  ratingCount={toolData.ratingCount}
                  size="sm"
                />
              )}

            {isExternal && (
              <span className="flex items-center gap-1 text-primary-600 dark:text-primary-400 font-medium">
                <ExternalLinkIcon className="w-3.5 h-3.5" />
                Visit
              </span>
            )}
          </div>

          {/* Bookmark button */}
          {showUserData && toolWithUserData && (
            <BookmarkButton
              resourceId={tool.id}
              isBookmarked={toolWithUserData.isBookmarked}
              size="sm"
            />
          )}
        </div>
      </div>
    </div>
  );

  const cardClasses =
    "group block h-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-200";

  // All tools link to detail page (where reviews are shown)
  // External tools have a "Visit Website" button on the detail page
  return (
    <Link to={`/tools/${tool.id}`} className={cardClasses}>
      {cardContent}
    </Link>
  );
}
